import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth-context';
import { LOGIN, REGISTER } from '@/constants/testIds';
import { Mail, Lock, User, Loader2, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

export default function Login() {
  const [mode, setMode] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [slowNotice, setSlowNotice] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSlowNotice(false);
    const slowTimer = setTimeout(() => setSlowNotice(true), 2500);
    try {
      if (mode === 'login') {
        await login(email, password);
        toast.success('Welcome back');
      } else {
        if (password.length < 6) { toast.error('Password must be at least 6 characters'); setSubmitting(false); return; }
        await register(email, password, name || email.split('@')[0]);
        toast.success('Account created');
      }
      navigate('/');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Authentication failed');
    } finally {
        clearTimeout(slowTimer);
      setSlowNotice(false);
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-6 py-12 bg-gradient-to-b from-white to-slate-50">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="font-serif-display text-5xl text-slate-900 mb-2">
            {mode === 'login' ? 'Welcome back' : 'Create an account'}
          </h1>
          <p className="text-sm text-slate-600">
            {mode === 'login' ? 'Sign in to track bookmarks and flashcard progress.' : 'Join to save bookmarks and personalize your study deck.'}
          </p>
        </div>
        <div className="bg-white border border-slate-200 rounded-2xl p-8 shadow-sm">
          <form onSubmit={onSubmit} className="space-y-4">
            {mode === 'register' && (
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Name</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input type="text" data-testid={REGISTER.nameInput} value={name} onChange={(e) => setName(e.target.value)} placeholder="Jane Smith" className="input-base pl-10 py-2.5" />
                </div>
              </div>
            )}
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input type="email" data-testid={mode === 'login' ? LOGIN.emailInput : REGISTER.emailInput} value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" required className="input-base pl-10 py-2.5" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input type="password" data-testid={mode === 'login' ? LOGIN.passwordInput : REGISTER.passwordInput} value={password} onChange={(e) => setPassword(e.target.value)} placeholder={mode === 'register' ? 'At least 6 characters' : '••••••••'} required className="input-base pl-10 py-2.5" />
              </div>
            </div>
            <button type="submit" disabled={submitting} data-testid={mode === 'login' ? LOGIN.submitButton : REGISTER.submitButton} className="btn-primary w-full disabled:opacity-60">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <>{mode === 'login' ? 'Sign in' : 'Create account'} <ArrowRight className="w-4 h-4" /></>}
            </button>
                {submitting && slowNotice && (
            <p className="text-center text-xs text-slate-500 mt-3">Still working — first sign-in can take up to 60 seconds while our server wakes up.</p>
            )}
          </form>
          <p className="text-center text-sm text-slate-500 mt-6">
            {mode === 'login' ? (
              <>New to Scripture Souls?{' '}
                <button onClick={() => setMode('register')} data-testid={LOGIN.registerLink} className="text-[#1E3A8A] font-medium hover:underline">Create an account</button>
              </>
            ) : (
              <>Already have an account?{' '}
                <button onClick={() => setMode('login')} data-testid={REGISTER.loginLink} className="text-[#1E3A8A] font-medium hover:underline">Sign in</button>
              </>
            )}
          </p>
        </div>
        <p className="text-center text-xs text-slate-400 mt-6">
          <Link to="/" className="hover:text-slate-600">← Continue browsing without an account</Link>
        </p>
      </div>
    </div>
  );
}
